import LogService from "../CommonSdk/LogService";
import * as ErrorUtils from "../Utils/ErrorUtils";
import * as constants from "../Constants/SdkConstant";

const LOG_TAG = "SERVICE:APPLICATIONS";

class ApplicationsService {
    constructor(serverInstance) {
        LogService.debug(LOG_TAG, "constructor", "instanciated with:", serverInstance);
        this.serverInstance = serverInstance;
    }

    reset() {
        this.serverInstance = null;
    }

    getApplicationsPerPage(page, limit, criteria) {
        LogService.trace(LOG_TAG, "getApplicationsPerPage");

        let queryParameters = "?offset=" + String(limit * page);
        queryParameters += "&limit=" + Math.min(limit, constants.MAX_PAGE_SIZE);
        let hasFormatCriteria = false;

        for (let key in criteria) {
            queryParameters += "&" + key + "=" + criteria[key];
            if (key === "format") {
                hasFormatCriteria = true;
            }
        }

        if (!hasFormatCriteria) {
            queryParameters += "&format=medium";
        }

        return this.serverInstance.get("/api/rainbow/applications/v1.0/applications" + queryParameters)
            .then(response => {
                LogService.info(LOG_TAG, "getApplicationsPerPage", "success");
                return Promise.resolve(response);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "getApplicationsPerPage", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    getApplication(appId) {
        LogService.trace(LOG_TAG, "getApplication");

        return this.serverInstance.get("/api/rainbow/applications/v1.0/applications/" + appId)
            .then(response => {
                LogService.info(LOG_TAG, "getApplication", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "getApplication", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    updateApplication(appId, criteria) {
        LogService.trace(LOG_TAG, "updateApplication");

        const body = JSON.stringify(criteria);

        return this.serverInstance.put("/api/rainbow/applications/v1.0/applications/" + appId, body)
            .then(response => {
                LogService.info(LOG_TAG, "updateApplication", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "updateApplication", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    createApplication(criteria) {
        LogService.trace(LOG_TAG, "createApplication");

        const body = JSON.stringify(criteria);

        return this.serverInstance.post("/api/rainbow/applications/v1.0/applications", body)
            .then(response => {
                LogService.info(LOG_TAG, "createApplication", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "createApplication", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    deleteApplication(appId) {
        LogService.trace(LOG_TAG, "deleteApplication");

        return this.serverInstance.delete("/api/rainbow/applications/v1.0/applications/" + appId)
            .then(response => {
                LogService.info(LOG_TAG, "deleteApplication", "success");
                return Promise.resolve();
            })
            .catch(error => {
                LogService.error(LOG_TAG, "deleteApplication", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    deployApplicationRequest(appId) {
        LogService.trace(LOG_TAG, "deployApplicationRequest");

        return this.serverInstance.put("/api/rainbow/applications/v1.0/applications/" + appId + "/request-deploy")
            .then(response => {
                LogService.info(LOG_TAG, "deployApplicationRequest", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "deployApplicationRequest", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    deployApplication(appId, reason) {
        LogService.trace(LOG_TAG, "deployApplication");

        const body = JSON.stringify({ deployReason: reason });

        return this.serverInstance.put("/api/rainbow/applications/v1.0/applications/" + appId + "/deploy", body)
            .then(response => {
                LogService.info(LOG_TAG, "deployApplication", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "deployApplication", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    declineApplication(appId) {
        LogService.trace(LOG_TAG, "declineApplication");

        return this.serverInstance.put("/api/rainbow/applications/v1.0/applications/" + appId + "/decline")
            .then(response => {
                LogService.info(LOG_TAG, "declineApplication", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "declineApplication", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    stopApplication(appId) {
        LogService.trace(LOG_TAG, "stopApplication");

        return this.serverInstance.put("/api/rainbow/applications/v1.0/applications/" + appId + "/stop")
            .then(response => {
                LogService.info(LOG_TAG, "stopApplication", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "stopApplication", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    restartApplication(appId) {
        LogService.trace(LOG_TAG, "restartApplication");

        return this.serverInstance.put("/api/rainbow/applications/v1.0/applications/" + appId + "/restart")
            .then(response => {
                LogService.info(LOG_TAG, "restartApplication", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "restartApplication", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    blockApplication(appId) {
        LogService.trace(LOG_TAG, "blockApplication");

        return this.serverInstance.put("/api/rainbow/applications/v1.0/applications/" + appId + "/block")
            .then(response => {
                LogService.info(LOG_TAG, "blockApplication", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "blockApplication", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    unblockApplication(appId) {
        LogService.trace(LOG_TAG, "unblockApplication");

        return this.serverInstance.put("/api/rainbow/applications/v1.0/applications/" + appId + "/unblock")
            .then(response => {
                LogService.info(LOG_TAG, "unblockApplication", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "unblockApplication", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    deletePushNotification(appId, pnId) {
        LogService.trace(LOG_TAG, "deletePushNotification");

        return this.serverInstance.delete("/api/rainbow/applications/v1.0/applications/" + appId + "/push-settings/" + pnId)
            .then(response => {
                LogService.info(LOG_TAG, "deletePushNotification", "success");
                return Promise.resolve();
            })
            .catch(error => {
                LogService.error(LOG_TAG, "deletePushNotification", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    addPushNotification(appId, type, isEnabled, key, senderId, certificateType, certificateFile, appBundleId) {
        LogService.trace(LOG_TAG, "addPushNotification");

        let bodyParameters = {
            type: type,
            isEnabled: isEnabled
        };
        if (key) {
            bodyParameters = { ...bodyParameters, authorizationKey: key };
        }
        if (senderId) {
            bodyParameters = { ...bodyParameters, senderId: senderId };
        }
        if (certificateType) {
            bodyParameters = { ...bodyParameters, certificateType: certificateType };
        }
        if (certificateFile) {
            bodyParameters = { ...bodyParameters, certificateFile: certificateFile };
        }
        if (appBundleId) {
            bodyParameters = { ...bodyParameters, appBundleId: appBundleId };
        }

        const body = JSON.stringify(bodyParameters);

        return this.serverInstance.post("/api/rainbow/applications/v1.0/applications/" + appId + "/push-settings", body)
            .then(response => {
                LogService.info(LOG_TAG, "addPushNotification", "success");
                return Promise.resolve();
            })
            .catch(error => {
                LogService.error(LOG_TAG, "addPushNotification", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    updatePushNotification(appId, pnId, isEnabled, key, senderId, certificateType, certificateFile, appBundleId) {
        LogService.trace(LOG_TAG, "updatePushNotification");

        let bodyParameters = {
            isEnabled: isEnabled
        };
        if (key) {
            bodyParameters = { ...bodyParameters, authorizationKey: key };
        }
        if (senderId) {
            bodyParameters = { ...bodyParameters, senderId: senderId };
        }
        if (certificateType) {
            bodyParameters = { ...bodyParameters, certificateType: certificateType };
        }
        if (certificateFile) {
            bodyParameters = { ...bodyParameters, certificateFile: certificateFile };
        }
        if (appBundleId) {
            bodyParameters = { ...bodyParameters, appBundleId: appBundleId };
        }

        const body = JSON.stringify(bodyParameters);

        return this.serverInstance.put("/api/rainbow/applications/v1.0/applications/" + appId + "/push-settings/" + pnId, body)
            .then(response => {
                LogService.info(LOG_TAG, "updatePushNotification", "success");
                return Promise.resolve();
            })
            .catch(error => {
                LogService.error(LOG_TAG, "updatePushNotification", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    registerDeveloper() {
        LogService.trace(LOG_TAG, "registerDeveloper");

        return this.serverInstance.post("/api/rainbow/applications/v1.0/developers/register?withoutConfirmationEmail")
            .then(response => {
                LogService.info(LOG_TAG, "registerDeveloper", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "registerDeveloper", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }

    unregisterDeveloper(loginEmail) {
        LogService.trace(LOG_TAG, "unregisterDeveloper");

        return this.serverInstance.post("/api/rainbow/applications/v1.0/developers/unregister?loginEmail=" + loginEmail)
            .then(response => {
                LogService.info(LOG_TAG, "unregisterDeveloper", "success");
                return Promise.resolve(response.data);
            })
            .catch(error => {
                LogService.error(LOG_TAG, "unregisterDeveloper", "error", error);
                return Promise.reject(ErrorUtils.parseErrorAndReturnPayload(error));
            });
    }
}

export default ApplicationsService;